import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Manufacturing Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Manufacturing was one of
 * the eight still carrying it: a two-paragraph series description under an amber note
 * saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*,
 * the same words the Pharmaceuticals, Public Services, Retail, Services, Technology
 * and Tourism cuts arrived in — with a four-part full-page capture of digitopia.co's
 * Manufacturing Industry Focus page as the reference, plus four files: the report
 * mockup, Figure 1, and two overlapping crops of the cover. So this is the
 * twenty-eighth designed report page and the thirteenth focus cut to arrive with
 * **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiPharmaceuticals.ts`
 * and `content/daimiRetail.ts` do, and `daimiManufacturingChecks()` feeds them into
 * the same manifest as the rest of the site's amber notes so a production build cannot
 * ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That band is
 * `content/reportClosing.ts`, shared by every report page but three, and it already
 * points at the Digital & AI Maturity Report 2026 — so this page inherits the answer
 * rather than restating it, and the reference capture agrees: its Global Perspective
 * card names the 2026 report too, and names this cut "The Manufacturing Focus Report",
 * which is the `shortName` `focus()` already gives it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These documents
 * are prerendered with no client bundle at all (see `src/prerender.tsx`), so an
 * embedded form script would never run, and the other reports on this site hand the
 * reader the PDF from our own bucket instead. So every Download button here is that
 * PDF, and `DAIMI_MF_DOWNLOAD.check` is what keeps the question of gating open.
 *
 * **Not to be confused with the Future 2030 Manufacturing outlook**, which is a
 * separate report with its own slug, its own cover and its own designed page
 * (`content/outlookManufacturing.ts`, `pages/OutlookManufacturing.tsx`) — which is why
 * the constants here are `DAIMI_MF_*` and that page's are `OUTLOOK_MF_*`. Two reports,
 * two pages, one sector.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound links
 * this page exists to keep working.
 */
export const DAIMI_MF_SLUG = 'digital-and-ai-maturity-report-2026-manufacturing-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own "to lead in
 * 2026" line instead, and it is the page's argument in one sentence.
 *
 * The 3.0 in the first paragraph is set in bold on the live page. It is plain here,
 * because this page renders its paragraphs as strings rather than as markup — and it
 * is the one number the page can check against itself: 3.00 is also the 2025 average
 * in Figure 1, and the sector sitting exactly on the cross-industry baseline is what
 * the sentence is about.
 */
export const DAIMI_MF_INTRO = {
  body: [
    'Manufacturing scores 3.0 in the 2026 DAIMI benchmark, aligning precisely with the cross-industry baseline. The industry’s powerful execution DNA, rooted in continuous improvement and complex value chain management, creates massive potential for AI to impact throughput, yield, and downtime.',
    'To lead in 2026, organizations must move beyond the noise of isolated AI pilots and industrialize transformation with the same discipline they apply to their production lines.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply gets
   * a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction, Utilities, Tourism, Technology, Services, Retail, Public Services
   * and Pharmaceuticals cuts made.
   *
   * **With the same caveat those covers had, file for file.** It arrived as two
   * captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one cut off at the top through the title's margin. The second is the
   * one held here, because it is the only one that contains the whole design; it is
   * 2000 × 1848 rather than square, so it is missing a sliver of the top margin, and
   * the index card's square crop trims about 4% off each side. `check` asks for the
   * file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-manufacturing-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Manufacturing Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 3.0 in the first one, which the capture sets in bold and which is also Figure 1’s 2025 average, and the cross-industry baseline it says the sector aligns with “precisely”, which is a figure the capture never prints and so cannot be checked from the files supplied. Unlike most of the focus cuts, this page’s live hero does carry the cover beside the words, so the cover here is a match rather than the addition `pages/FocusPage.tsx` makes elsewhere. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the fuller one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "The Scaling Barrier:", "Industrializing AI:".
 * Split into `label` and `body` here so the label can be set rather than emphasised
 * inside a sentence, which is what a `<strong>` in the middle of a paragraph gives.
 *
 * The heading is "DAIMI Findings" and nothing after it: unlike the Pharmaceuticals
 * cut, this page's live heading carries no subtitle, so none is invented here.
 */
export const DAIMI_MF_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'Manufacturing maturity is currently characterized by high variability, where strong shop-floor execution often lacks the enterprise backbone needed for global standardization.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest where processes are tangible and measurable, particularly in predictive maintenance, quality inspection, and production optimization.',
    },
    {
      label: 'The Scaling Barrier',
      body: 'Momentum is frequently hindered by disconnected OT and IT environments and inconsistent master data across plants, preventing local wins from becoming enterprise-wide outcomes.',
    },
    {
      label: 'Industrializing AI',
      body: 'Leading manufacturers treat digital and AI as a core industrial capability that is integrated directly into daily workflows, rather than as a side innovation project.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request, the
   * same shape the Automotive, Banking, Services, Retail and Pharmaceuticals cuts
   * carry rather than the vitality scatter the Technology, Tourism and Public Services
   * cuts do or the portrait score range the Utilities cut does. Six years, 2020 to
   * 2025, each one a range from the laggard through the average to the leader with
   * that year's company count in the header. It is 1200 × 608 on a white ground, so it
   * gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-manufacturing-study-results.webp',
    alt: 'DAIMI study results for the manufacturing industry, 2020 to 2025: the laggard, average and leader score each year, from 1.70–2.73–3.60 in 2020 to 2.32–3.00–3.82 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Manufacturing Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (20 in 2020 rising to 139 in 2024 and 136 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 3.00, is the same number the hero opens with, which is the one cross-check the page can do on itself; note that the average has drifted *down* across the last three years (3.07 → 3.04 → 3.00) while the laggard rose sharply in 2025 (1.80 to 2.32), so the spread is narrowing rather than the sector improving — neither of which the prose mentions, and the “high variability” the lede opens with is least true of the most recent year in the chart. What wants a reading against digitopia.co is that prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_MF_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Manufacturing',
  body: [
    'To lead in 2026, manufacturing executives must establish a repeatable “factory for scaling” that moves solutions from proof-of-concept to global production. Success requires a disciplined focus on connecting OT and IT architectures and investing in role-based upskilling to ensure technology actually translates into floor-level performance.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn industrial discipline into a compounding digital advantage.',
  ],
  /**
   * The mockup supplied with this request — the manufacturing cover, not another
   * cut's. Unlike most of the focus cuts, the reference capture *does* show it: it
   * sits at the right of this band on the live page, which is where it is here.
   */
  photo: {
    src: '/photo/daimi-report-2026-manufacturing-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Manufacturing Focus',
  } satisfies Photo,
  check:
    'The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence, and whose “factory for scaling” is in quotation marks on the live page and in curly quotes here. The mockup beside them is the file you attached and it is where the live page puts it, so nothing about this band is an addition.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every report
 * page but three — and the reference capture for this page shows exactly that pair,
 * pointing at the Digital & AI Maturity Report 2026, which is the "2024 değil 2026"
 * the request asked for. Nothing to change: the shared band already links to
 * `DAIMI_SLUG`, and its opening sentence already names this cut "The Manufacturing
 * Focus Report" — the capture says "The Manufacturing Focus Report" too, which is the
 * `shortName` `focus()` mints.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_MF_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Manufacturing Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiPharmaceuticalsChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut arrived
 * with both of its figures and its cover. Three of the notes are a transcription
 * nobody has read against the original, and one is a decision about gating that
 * belongs to marketing.
 */
export function daimiManufacturingChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_MF_INTRO.check },
    { section: 'findings', check: DAIMI_MF_FINDINGS.check },
    { section: 'analysis', check: DAIMI_MF_ANALYSIS.check },
    { section: 'download', check: DAIMI_MF_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_MF_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
